import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { NestedFullscreenDialog } from '@/components/ui/nested-fullscreen-dialog';
import {
  Section,
  SectionRowComparison,
  SectionLeftColumn,
  SectionTitle,
  SectionDescription,
  SectionComparisonBeforeColumn,
  SectionComparisonAfterColumn,
} from '@/components/shared/SectionComponents';
import { ProposalActionButtons } from '@/components/ui/proposal-action-buttons';
import { useProposalExecutionState } from '@/components/execution/use-proposal-execution-state';
import { useAuthClient } from '@/lib/authClient';
import { formatName } from '@/lib/name';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useListMembers } from '@/hooks/organization/use-list-members';
import { useActiveAdminChangeProposal } from '@/hooks/admin/organization/use-active-admin-change-proposal';
import { useAdminChangeProposalSignature } from '@/hooks/admin/organization/use-admin-change-proposal-signature';
import { useExecuteAdminChangeProposal } from '@/hooks/admin/organization/use-execute-admin-change-proposal';
import { PendingSignaturesModal } from '@/components/modals/pending-signatures-modal';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import {
  getSignatureCountsAndUserVote,
  canExecuteRejection as canExecuteRejectionForProposal,
} from '@/utils/SignatureCountsHelpers';
import {
  getInvalidProposalDisplayMessage,
  OrgOperationDecision,
  type OrgOpSignableDecision,
  type ExecuteAction,
} from '@mls-wallet/shared-types';
import { SIGNABLE_POLL_INTERVAL_MS } from '@/components/execution/execution-polling';

interface AdminChangeComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdminChangeComparisonDialog({
  open,
  onOpenChange,
}: AdminChangeComparisonDialogProps) {
  const { user } = useAuthClient();
  const { organization, refetch: refreshOrganization } = useMyOrganization();

  const {
    pendingSignableIds,
    setPendingSignableIds,
    pendingDecision,
    setPendingDecision,
    isPendingModalOpen,
    setIsPendingModalOpen,
    resetPendingState,
  } = usePendingSignaturesState();

  const { signProposal, isLoading: isSigning } =
    useAdminChangeProposalSignature({
      onError: (error) => {
        console.error('Failed to create signature', error);
        resetPendingState();
      },
    });
  const { executeProposal, isLoading: isExecuteLoading } =
    useExecuteAdminChangeProposal();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Proposal eligibility must continue polling during execution so another
  // user's state is never hidden by this browser's mutation lifecycle.
  const proposalRefreshInterval = open ? SIGNABLE_POLL_INTERVAL_MS : 0;
  const memberRefreshInterval = open ? 10_000 : 0;

  const { members, refresh: refreshMembers } = useListMembers(
    organization?.id ?? null,
    {
      refreshInterval: memberRefreshInterval,
    }
  );
  const {
    activeProposal: proposedChange,
    refresh: refreshActiveProposal,
    isLoading: isLoadingProposal,
  } = useActiveAdminChangeProposal({
    refreshInterval: proposalRefreshInterval,
  });
  const proposalId = proposedChange?.proposal.id ?? null;
  const { isExecuting: isProposalExecuting, startExecution } =
    useProposalExecutionState({
      proposalId,
      isServerExecuting: proposedChange?.isExecuting ?? false,
      isRequestPending: isExecuteLoading,
    });

  const memberById = useMemo(
    () => new Map(members.map((m) => [m.id, m])),
    [members]
  );
  const resolveUserName = (userId: string) =>
    memberById.get(userId)?.name ?? userId;

  const currentAdmins = useMemo(() => {
    const adminUserIds = organization?.adminUsers ?? [];
    return adminUserIds.map((id) => ({ id, name: resolveUserName(id) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.adminUsers, memberById]);

  const currentApprovalsNeeded = organization?.numberOfApprovalsNeeded ?? 1;

  const proposedByName = proposedChange
    ? resolveUserName(proposedChange.proposal.createdByUserId)
    : '';

  const { numApprovals, numRejections, hasApproved, hasRejected } = useMemo(
    () =>
      getSignatureCountsAndUserVote({
        approvals: proposedChange?.approvals ?? [],
        rejections: proposedChange?.rejections ?? [],
        userId: user?.id,
        requiredNetworkIds: proposedChange?.requiredNetworkIds ?? [],
      }),
    [
      proposedChange?.approvals,
      proposedChange?.rejections,
      proposedChange?.requiredNetworkIds,
      user?.id,
    ]
  );

  const refreshOrgSettings = async () => {
    await Promise.all([
      refreshActiveProposal(),
      refreshOrganization(),
      refreshMembers(),
    ]);
  };

  if (!user) return null;

  const canExecute =
    !!proposedChange &&
    proposedChange.proposal.status === 'PENDING' &&
    numApprovals >= proposedChange.requiredApprovals;

  // Check if proposal can be rejected (enough rejections)
  const canExecuteRejection =
    !!proposedChange &&
    canExecuteRejectionForProposal({
      status: proposedChange.proposal.status,
      numRejections,
      requiredApprovals: proposedChange.requiredApprovals,
    });

  const submitReview = async (decision: OrgOpSignableDecision) => {
    if (!proposedChange) return;
    setErrorMessage(null);
    try {
      // Create signature
      await signProposal({
        proposalId: proposedChange.proposal.id,
        decision,
      });
      // Refresh proposal so the frontend knows the signature record has been created, but proposal hasn't been actually signed yet
      // This way, we can close the modal when either the proposal is signed (confirmed) or the signature record is deleted (cancelled)
      await refreshOrgSettings();
      // Open the pending signatures modal
      setPendingSignableIds([proposedChange.proposal.id]);
      setPendingDecision(decision);
      setIsPendingModalOpen(true);
    } catch (err) {
      console.error(
        `Failed to ${decision.toLowerCase()} admin-change proposal`,
        err
      );
      setErrorMessage(
        err instanceof Error
          ? err.message
          : `Failed to ${decision.toLowerCase()} admin-change proposal.`
      );
      resetPendingState();
    }
  };

  /**
   * Handle execute button click for both approval and rejection.
   * Both APPROVE and REJECT create a GuardianTransaction and are processed via
   * the async worker. The modal polls until completion and handles callbacks.
   */
  const handleExecute = (action: ExecuteAction) => {
    // Case: return when proposal data or its ID is missing, or execution is active.
    if (!proposedChange || !proposalId || isProposalExecuting) return;

    // Case: execution requires the signature threshold for the requested action.
    if (action === 'APPROVE' && !canExecute) return;
    if (action === 'REJECT' && !canExecuteRejection) return;

    setErrorMessage(null);
    startExecution({
      executionTarget: 'proposal',
      executionTargetId: proposalId,
      proposalType: 'admin change',
      request: () => executeProposal({ proposalId, action }),
      onAlreadyExecuting: async () => {
        await refreshOrgSettings();
      },
      onSuccess: async () => {
        await refreshOrgSettings();
        onOpenChange(false);
      },
    });
  };

  const isLoading = isSigning || isLoadingProposal;

  const beforeAdmins = currentAdmins;
  const afterAdmins = (() => {
    if (!proposedChange) return beforeAdmins;
    const next = new Map(beforeAdmins.map((a) => [a.id, a]));
    for (const p of proposedChange.proposal.adminUserProposals) {
      if (p.action === 'ADD') {
        if (!next.has(p.userId))
          next.set(p.userId, { id: p.userId, name: resolveUserName(p.userId) });
      } else {
        next.delete(p.userId);
      }
    }
    return Array.from(next.values());
  })();

  const isAdminsChanged = (() => {
    if (beforeAdmins.length !== afterAdmins.length) return true;
    const beforeIds = beforeAdmins
      .map((a) => a.id)
      .slice()
      .sort()
      .join(',');
    const afterIds = afterAdmins
      .map((a) => a.id)
      .slice()
      .sort()
      .join(',');
    return beforeIds !== afterIds;
  })();

  const beforeThresholdRaw = Math.max(1, currentApprovalsNeeded);
  const beforeThreshold = Math.min(
    beforeThresholdRaw,
    Math.max(1, beforeAdmins.length)
  );

  const afterThresholdRaw = Math.max(
    1,
    proposedChange?.proposal.numberOfApprovalsNeeded ?? currentApprovalsNeeded
  );
  const afterThreshold = Math.min(
    afterThresholdRaw,
    Math.max(1, afterAdmins.length)
  );
  const isThresholdChanged =
    beforeThreshold !== afterThreshold ||
    beforeAdmins.length !== afterAdmins.length;

  const renderNames = (admins: { id: string; name: string }[]) => {
    if (admins.length === 0) {
      return <span className="text-sm text-muted-foreground">None</span>;
    }
    return (
      <div className="text-sm font-medium leading-6 space-y-1">
        {admins.map((a) => (
          <div key={a.id}>{formatName(a.name)}</div>
        ))}
      </div>
    );
  };

  const footer = isLoading ? (
    <div className="flex justify-center py-4">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  ) : (
    <div className="space-y-2">
      {errorMessage ? (
        <div className="text-sm text-rose-600">{errorMessage}</div>
      ) : null}
      <ProposalActionButtons
        onReject={() => submitReview(OrgOperationDecision.REJECT)}
        onApprove={() => submitReview(OrgOperationDecision.APPROVE)}
        onExecuteRejection={() => handleExecute('REJECT')}
        onExecute={() => handleExecute('APPROVE')}
        hasApproved={hasApproved}
        hasRejected={hasRejected}
        canExecuteRejection={canExecuteRejection}
        canExecute={canExecute}
        isExecuting={isProposalExecuting}
        disabled={
          !proposedChange || proposedChange.proposal.status !== 'PENDING'
        }
        approveDisabled={Boolean(
          proposedChange && proposedChange.proposal.validity !== 'valid'
        )}
        approveDisabledReason={
          proposedChange?.proposal.validity &&
          proposedChange.proposal.validity !== 'valid'
            ? getInvalidProposalDisplayMessage(
                proposedChange.proposal.domainInvalidReason
              )
            : undefined
        }
      />
    </div>
  );

  return (
    <NestedFullscreenDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Proposed changes to Admins"
      footer={footer}
      bodyContainerClassName="bg-muted"
    >
      <div className="p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          {!proposedChange ? (
            <div className="bg-card border border-border rounded-lg p-4 text-sm text-muted-foreground">
              No proposed changes pending review.
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center divide-x divide-border">
                <div className="pr-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Proposed by
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">
                    {formatName(proposedByName)}
                  </span>
                </div>
                <div className="px-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Proposed on
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">
                    {new Date(
                      proposedChange.proposal.createdAt
                    ).toLocaleString()}
                  </span>
                </div>
                <div className="px-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Approvals
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">
                    {numApprovals}/{proposedChange.requiredApprovals}
                  </span>
                </div>
                <div className="pl-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Rejections
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">{numRejections}</span>
                </div>
              </div>
            </div>
          )}

          <Section title="Admins (before / after)">
            <SectionRowComparison>
              <SectionLeftColumn>
                <SectionTitle>Admins</SectionTitle>
                <SectionDescription>
                  Proposed admin set changes (before vs after)
                </SectionDescription>
              </SectionLeftColumn>

              <SectionComparisonBeforeColumn highlight={isAdminsChanged}>
                {renderNames(beforeAdmins)}
              </SectionComparisonBeforeColumn>

              <SectionComparisonAfterColumn highlight={isAdminsChanged}>
                {renderNames(afterAdmins)}
              </SectionComparisonAfterColumn>
            </SectionRowComparison>

            <SectionRowComparison>
              <SectionLeftColumn>
                <SectionTitle>Voting threshold</SectionTitle>
                <SectionDescription>
                  Number of admin approvals required to execute this change
                </SectionDescription>
              </SectionLeftColumn>

              <SectionComparisonBeforeColumn highlight={isThresholdChanged}>
                <div className="text-sm font-medium">
                  {beforeThreshold} of {Math.max(1, beforeAdmins.length)} admin
                  {Math.max(1, beforeAdmins.length) === 1 ? '' : 's'}
                </div>
              </SectionComparisonBeforeColumn>

              <SectionComparisonAfterColumn highlight={isThresholdChanged}>
                <div className="text-sm font-medium">
                  {afterThreshold} of {Math.max(1, afterAdmins.length)} admin
                  {Math.max(1, afterAdmins.length) === 1 ? '' : 's'}
                </div>
              </SectionComparisonAfterColumn>
            </SectionRowComparison>
          </Section>
        </div>
      </div>

      {/* Pending Signature Modal */}
      <PendingSignaturesModal
        open={isPendingModalOpen}
        resetPendingState={resetPendingState}
        refreshSignables={refreshOrgSettings}
        pendingSignableIds={pendingSignableIds}
        polledSignables={
          proposedChange
            ? [
                {
                  id: proposedChange.proposal.id,
                  approvals: proposedChange.approvals,
                  rejections: proposedChange.rejections,
                  requiredApprovals: proposedChange.requiredApprovals,
                  initiatorSignature: undefined,
                },
              ]
            : []
        }
        pendingDecision={pendingDecision}
        hookFunctionToPoll={useActiveAdminChangeProposal}
        isTransaction={false}
      />
    </NestedFullscreenDialog>
  );
}
